import { useState } from 'react';

import {
  Button,
  Checkbox,
  Group,
  Modal,
  MultiSelect,
  Select,
  Stack,
  TagsInput,
  Text,
} from '@mantine/core';

import { ConversationAttachment } from '@/types/attachment';
import { EnquiryExtractConvertRequest } from '@/types/extraction';

interface CreateReportRequestModalProps {
  opened: boolean;
  onClose: () => void;
  /** ReportConfigs for the enquiry's team. */
  configOptions: { value: string; label: string }[];
  configsLoading: boolean;
  /** Conversation files offered as attachments for the new RR. */
  files: ConversationAttachment[];
  teamId: string | null;
  /** When the enquiry is already triaged, create appends instead of converts. */
  alreadyTriaged: boolean;
  isSubmitting: boolean;
  /** Same plumbing as the extraction flow — the page passes
   * `extractConvert(body, append)`. */
  onCreate: (body: EnquiryExtractConvertRequest, append: boolean) => Promise<void>;
}

/**
 * Manual report-request creation — the escape hatch when AI extraction
 * has nothing to work with ("see attached") or the analyst is simply
 * faster by hand. Submits a single fully-specified candidate
 * (`candidate_index: -1`) through the exact same convert/append endpoint
 * the extraction review uses, so provenance-free manual RRs and
 * extracted RRs are indistinguishable downstream.
 */
export function CreateReportRequestModal({
  opened,
  onClose,
  configOptions,
  configsLoading,
  files,
  teamId,
  alreadyTriaged,
  isSubmitting,
  onCreate,
}: Readonly<CreateReportRequestModalProps>) {
  const [configId, setConfigId] = useState<string | null>(null);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [expedited, setExpedited] = useState(false);
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);

  const fileOptions = files.map((f) => ({ value: f.id, label: f.filename }));

  const resetAndClose = () => {
    setConfigId(null);
    setSubjects([]);
    setExpedited(false);
    setAttachmentIds([]);
    onClose();
  };

  const submit = async () => {
    if (!configId) {
      return;
    }
    try {
      await onCreate(
        {
          candidates: [
            {
              candidate_index: -1, // manual — no stored candidate list
              report_config_id: configId,
              subjects: subjects.length > 0 ? subjects : null,
              jurisdictions: null,
              expedited,
              attachment_ids: attachmentIds,
              sources: [],
            },
          ],
          team_id: teamId,
        },
        alreadyTriaged
      );
    } catch {
      // The caller (useEnquiry.extractConvert) already toasted the
      // failure — stay open with the analyst's input intact.
      return;
    }
    resetAndClose();
  };

  return (
    <Modal opened={opened} onClose={resetAndClose} title="Create report request manually" size="lg">
      <Stack gap="md">
        {teamId === null && (
          <Text size="sm" c="orange">
            Assign a team to the enquiry before creating report requests — ReportConfigs are
            filtered per team.
          </Text>
        )}
        <Select
          label="Report config"
          placeholder="Choose a report type"
          data={configOptions}
          value={configId}
          onChange={setConfigId}
          searchable
          withAsterisk
          disabled={configsLoading}
          data-testid="manual-rr-config"
        />
        <TagsInput
          label="Subjects"
          description="Press Enter after each subject. Leave empty to fill in during triage."
          placeholder="Type a subject and press Enter"
          value={subjects}
          onChange={setSubjects}
          // One subject per line, matching the Define section's textarea —
          // the default splitChars include ',' which chops names like
          // "Acme, Inc." mid-typing.
          splitChars={['\n']}
          data-testid="manual-rr-subjects"
        />
        <MultiSelect
          label="Attach documents"
          placeholder="Conversation files to attach to this report request"
          data={fileOptions}
          value={attachmentIds}
          onChange={setAttachmentIds}
          searchable
          clearable
        />
        <Checkbox
          label="Expedited"
          checked={expedited}
          onChange={(e) => setExpedited(e.currentTarget.checked)}
          data-testid="manual-rr-expedited"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={resetAndClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={isSubmitting}
            disabled={!configId || !teamId}
            data-testid="manual-rr-create"
          >
            {alreadyTriaged ? 'Add report request' : 'Create report request'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
